import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { label: 'Home', page: 'Home' },
  { label: 'About', page: 'About' },
  { label: 'Services', page: 'Services' },
  { label: '✨ Try Demos', page: 'Demos', highlight: true },
  { label: 'Contact', page: 'Contact' },
];

export default function Navbar({ currentPageName }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-slate-950/90 backdrop-blur-xl border-b border-slate-800/50' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to={createPageUrl('Home')} className="flex items-center gap-3">
          <img src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/69b2907c6ea2c641fd46240c/8e8026a6b_ChatGPTImageMar12202603_24_58AM.png" alt="Bridge Logo" className="w-9 h-9 object-contain" />
          <span className="text-white text-xl font-bold tracking-tight">Bridge</span>
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map(link => (
            <Link
              key={link.page}
              to={createPageUrl(link.page)}
              className={`text-sm font-medium transition-colors ${
                link.highlight 
                  ? 'text-emerald-400 hover:text-emerald-300 font-semibold'
                  : currentPageName === link.page
                  ? 'text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link to={createPageUrl('Contact')}>
            <button className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors">
              Get Started
            </button>
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-white p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-slate-950/95 backdrop-blur-xl border-b border-slate-800"
          >
            <div className="px-6 py-6 space-y-4">
              {navLinks.map(link => (
                <Link
                  key={link.page}
                  to={createPageUrl(link.page)}
                  onClick={() => setMobileOpen(false)}
                  className={`block text-base font-medium ${
                    link.highlight
                      ? 'text-emerald-400 font-semibold'
                      : currentPageName === link.page 
                      ? 'text-white' 
                      : 'text-slate-400'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link to={createPageUrl('Contact')} onClick={() => setMobileOpen(false)}>
                <button className="w-full mt-2 px-5 py-3 bg-blue-500 text-white text-sm font-medium rounded-lg">
                  Get Started
                </button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
